import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import DealerProfile from '../components/admin/DealerProfile';
import KnowledgeCenter from '../components/admin/KnowledgeCenter';
import StaffManagement from '../components/admin/StaffManagement';

import DMSConnections from '../components/admin/DMSConnections';
import SitesManagement from '../components/admin/SitesManagement';
import EmailSuppression from '../components/admin/EmailSuppression';
import CommsSetupWizard from '../components/admin/CommsSetupWizard';
import DealerGroupsManagement from '../components/admin/DealerGroupsManagement';
import AdvancedComms from '../components/admin/AdvancedComms';

function AdminDashboard({ initialTab = 'profile' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const loadUser = async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Dealer Group Profile', shortLabel: 'Profile', icon: '🏢', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'knowledge', label: 'Knowledge', shortLabel: 'Knowledge', icon: '🧠', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'staff', label: 'Users', shortLabel: 'Users', icon: '👥', roles: ['admin', 'manager', 'super_admin'] },

    { id: 'dms', label: 'DMS', shortLabel: 'DMS', icon: '🔗', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'sites', label: 'Sites', shortLabel: 'Sites', icon: '🏬', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'dealer_groups', label: 'Dealer Groups', shortLabel: 'Groups', icon: '🏢', roles: ['super_admin'] },
    { id: 'comms_setup', label: 'Comms Setup', shortLabel: 'Comms', icon: '📡', roles: ['super_admin'] },
    { id: 'advanced_comms', label: 'Advanced: Comms', shortLabel: 'Adv Comms', icon: '🧪', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'email_suppression', label: 'Email Suppression', shortLabel: 'Suppression', icon: '🚫', roles: ['admin', 'super_admin'] },
  ];

  // Filter tabs based on user role
  const visibleTabs = tabs.filter(tab => 
    user && tab.roles.includes(user.role)
  );

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <DealerProfile />;
      case 'knowledge':
        return <KnowledgeCenter />;
      case 'staff':
        return <StaffManagement />;

      case 'dms':
        return <DMSConnections />;
      case 'sites':
        return <SitesManagement />;
      case 'dealer_groups':
        return <DealerGroupsManagement />;
      case 'comms_setup':
        return <CommsSetupWizard />;
      case 'email_suppression':
        return <EmailSuppression />;
      case 'advanced_comms':
        return <AdvancedComms />;
      default:
        return <DealerProfile />;
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  const activeTabData = visibleTabs.find(t => t.id === activeTab);

  return (
    <div style={styles.container}>
      {/* Mobile Header */}
      <div style={styles.mobileHeader} className="mobile-only">
        <button 
          style={styles.mobileMenuBtn}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span style={styles.mobileMenuIcon}>☰</span>
          <span style={styles.mobileMenuText}>Menu</span>
        </button>
        <span style={styles.mobileTitle}>
          {activeTabData?.icon} {activeTabData?.shortLabel}
        </span>
        <div style={styles.mobileSpacer} />
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div style={styles.mobileDrawer} className="mobile-only">
          <div style={styles.mobileDrawerHeader}>
            <span style={styles.mobileDrawerTitle}>⚙️ Admin</span>
            <button 
              style={styles.mobileDrawerClose}
              onClick={() => setMobileMenuOpen(false)}
            >
              ✕
            </button>
          </div>
          <nav style={styles.mobileNav}>
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  ...styles.mobileNavButton,
                  ...(activeTab === tab.id ? styles.mobileNavButtonActive : {}),
                }}
              >
                <span style={styles.mobileNavIcon}>{tab.icon}</span>
                <span style={styles.mobileNavLabel}>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}


      {/* Main Content */}
      <div style={styles.content}>
        {renderContent()}
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-only { display: none !important; }
        }
        @media (min-width: 769px) {
          .mobile-only { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0f172a',
    flexDirection: 'column',
  },
  mobileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: '#1e293b',
    borderBottom: '1px solid #334155',
  },
  mobileMenuBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: '#334155',
    border: 'none',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    cursor: 'pointer',
    minHeight: '40px',
  },
  mobileMenuIcon: {
    fontSize: '16px',
  },
  mobileMenuText: {
    fontWeight: '500',
  },
  mobileTitle: {
    color: '#f1f5f9',
    fontSize: '16px',
    fontWeight: '600',
  },
  mobileSpacer: {
    width: '60px',
  },
  mobileDrawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#1e293b',
    zIndex: 200,
    display: 'flex',
    flexDirection: 'column',
  },
  mobileDrawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid #334155',
  },
  mobileDrawerTitle: {
    color: '#f1f5f9',
    fontSize: '18px',
    fontWeight: '600',
  },
  mobileDrawerClose: {
    padding: '8px',
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    fontSize: '20px',
    cursor: 'pointer',
  },
  mobileNav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    gap: '8px',
    flex: 1,
  },
  mobileNavButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'transparent',
    border: 'none',
    borderRadius: '10px',
    color: '#94a3b8',
    fontSize: '16px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  mobileNavButtonActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
  },
  mobileNavIcon: {
    fontSize: '20px',
  },
  mobileNavLabel: {
    fontWeight: '500',
  },
  sidebar: {
    width: '260px',
    background: '#1e293b',
    borderRight: '1px solid #334155',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  sidebarHeader: {
    padding: '20px',
    borderBottom: '1px solid #334155',
  },
  sidebarTitle: {
    color: '#f1f5f9',
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
  },
  nav: {
    flex: 1,
    padding: '16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    border: 'none',
    borderRadius: '8px',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'left',
    transition: 'all 0.2s',
  },
  navButtonActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
  },
  navIcon: {
    fontSize: '18px',
  },
  navLabel: {
    fontWeight: '500',
  },
  sidebarFooter: {
    padding: '16px',
    borderTop: '1px solid #334155',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  userRole: {
    fontSize: '11px',
    color: '#60a5fa',
    fontWeight: '600',
  },
  userName: {
    fontSize: '13px',
    color: '#f1f5f9',
  },
  content: {
    flex: 1,
    padding: '16px',
    overflow: 'auto',
    minHeight: 0,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#0f172a',
    color: '#f1f5f9',
  },
};

export default AdminDashboard;
